import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText, Send } from 'lucide-react';
import { agentAppAPI, configAPI } from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export default function AgentReceiptPage() {
  const { paymentId } = useParams();
  const { setHeader } = useOutletContext();
  const [payment, setPayment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [collecting, setCollecting] = useState(false);
  const [sendingBill, setSendingBill] = useState(false);
  // Agent app is cable-only — same 'catv' company identity the PDF receipt (buildBillHtml)
  // pulls its Terms and Conditions from, so this on-screen view and the shared PDF agree.
  const [terms, setTerms] = useState([]);
  useEffect(() => {
    configAPI.get().then(({ data }) => setTerms(data.data?.catv?.termsAndConditions || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await agentAppAPI.getPayment(paymentId);
      setPayment(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load receipt');
    }
  }, [paymentId]);

  useEffect(() => { load(); }, [load]);

  const isPaid = payment?.status === 'completed';
  useEffect(() => {
    if (payment) setHeader({ title: isPaid ? 'Payment Receipt' : 'Bill (Unpaid)', showBack: true });
  }, [setHeader, isPaid, payment]);

  if (!payment) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;

  const customer = payment.customer;

  // Sends the actual bill/receipt PDF on WhatsApp to the customer's own registered mobile
  // (never a custom number — this is the account-of-record's own document) — same
  // templated, gateway-logged send as the admin desktop's Balance History "Bill Share".
  const sendBillOnWhatsApp = async () => {
    setSendingBill(true);
    try {
      const { data } = await agentAppAPI.shareBillWhatsApp(paymentId);
      toast.success(data.message || 'Bill sent on WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send bill');
    } finally {
      setSendingBill(false);
    }
  };

  const openReceiptPdf = async () => {
    // window.open must fire synchronously inside the click handler or mobile browsers
    // silently block it as a popup once we're past the `await` below.
    const win = window.open('', '_blank');
    try {
      const { data } = await agentAppAPI.getReceiptBlob(paymentId);
      if (win) win.location.href = URL.createObjectURL(data);
    } catch {
      if (win) win.close();
      toast.error('Failed to load receipt');
    }
  };

  const collectNow = async () => {
    setCollecting(true);
    try {
      const { data } = await agentAppAPI.settlePayment(paymentId, { paymentMethod, notes: notes || undefined });
      toast.success(data.message || 'Payment collected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to collect payment');
    } finally {
      setCollecting(false);
    }
  };

  return (
    <div style={{ padding: 14 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
        <div style={{ textAlign: 'center', fontWeight: 700, marginBottom: 10 }}>
          {isPaid ? 'Payment Receipt' : 'Bill — Awaiting Collection'}
        </div>

        <Row label="Name" value={customer?.name} />
        <Row label="Address" value={customer?.address?.line1 || ''} />
        <Row label={isPaid ? 'Receipt No.' : 'B.No.'} value={payment.receiptNumber} />
        <Row label={isPaid ? 'Record Time' : 'Generated On'} value={new Date(payment.paymentDate).toLocaleString('en-GB')} />
        {payment.periodFrom && payment.periodTo && (
          <Row label="Period" value={`${new Date(payment.periodFrom).toLocaleDateString('en-GB')} – ${new Date(payment.periodTo).toLocaleDateString('en-GB')}`} />
        )}
        <Row label="Customer Code" value={customer?.customerId} last />

        <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {payment.items?.length > 0 ? (
            payment.items.map((it, i) => (
              <TableRow key={i} label={it.label} value={`${it.amount < 0 ? '-' : ''}₹${fmt(Math.abs(it.amount))}`} />
            ))
          ) : (
            <>
              <TableRow label="Amount" value={`₹${fmt(payment.amount)}`} />
              {payment.discount > 0 && <TableRow label="Discount" value={`-₹${fmt(payment.discount)}`} />}
              {payment.gstAmount > 0 && <TableRow label={`GST (${payment.gstPercent}%)`} value={`₹${fmt(payment.gstAmount)}`} />}
            </>
          )}
          <TableRow label={isPaid ? 'Total Paid' : 'Total Due'} value={`₹${fmt(payment.totalAmount)}`} bold last />
        </div>

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: '.85rem' }}>
          <div><b>Mode:</b> {payment.paymentMethod?.toUpperCase()}</div>
          {isPaid && <div><b>Collected By:</b> {payment.collectedBy?.name || '—'}</div>}
          {payment.notes && <div><b>Note:</b> {payment.notes}</div>}
        </div>

        {payment.signature ? (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <img src={payment.signature} alt="Customer signature" style={{ maxHeight: 60 }} />
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>Customer Signature</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 12 }}>
            This is a computer generated {isPaid ? 'receipt' : 'bill'}. It does not require any signature/stamp.
          </div>
        )}

        {terms.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: '.78rem', marginBottom: 4 }}>Terms and Conditions</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
              {terms.map((t, i) => <div key={i}>{i + 1}. {t}</div>)}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16 }}>
        <IconAction icon={FileText} label="View Bill" onClick={openReceiptPdf} />
        <IconAction
          icon={Send}
          label={sendingBill ? 'Sending…' : 'Send Bill via WhatsApp'}
          onClick={(customer?.mobile && !sendingBill) ? sendBillOnWhatsApp : undefined}
        />
      </div>

      {!isPaid && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Collect Payment</div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 10 }}>
            Amount due: <b>₹{fmt(payment.totalAmount)}</b> — collecting settles exactly this bill.
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mode</label>
            <select className="form-control" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="payment_gateway">Payment Gateway</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Comment</label>
            <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button
            onClick={collectNow} disabled={collecting}
            style={{ width: '100%', background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 24, padding: '12px 0', fontWeight: 700, cursor: collecting ? 'default' : 'pointer', opacity: collecting ? 0.6 : 1 }}
          >
            {collecting ? 'Working…' : 'Collect Payment'}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div style={{ display: 'flex', padding: '3px 0', borderBottom: last ? 'none' : '1px solid var(--border)', fontSize: '.82rem' }}>
      <div style={{ width: 110, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
      <div>: {value || '—'}</div>
    </div>
  );
}

function TableRow({ label, value, bold, last }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '8px 12px',
      borderBottom: last ? 'none' : '1px solid var(--border)', fontWeight: bold ? 700 : 500, fontSize: '.85rem'
    }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function IconAction({ icon: Icon, label, href, onClick }) {
  const content = (
    <>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px'
      }}>
        <Icon size={18} />
      </div>
      <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>{label}</div>
    </>
  );
  if (onClick) {
    return <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'center', cursor: 'pointer' }}>{content}</button>;
  }
  if (!href) return <div style={{ textAlign: 'center', opacity: 0.4 }}>{content}</div>;
  return <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', textAlign: 'center' }}>{content}</a>;
}
